'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import ArrowIcon from '@/fo/icons/base/icons/arrow.svg';
import BagIcon from '@/fo/icons/base/icons/bag-stroke.svg';
import CompareIcon from '@/fo/icons/base/icons/compare.svg';
import HeartIcon from '@/fo/icons/base/icons/heart.svg';
import UserIcon from '@/fo/icons/base/icons/login.svg';
import BrandLogo from '@/fo/icons/base/icons/logo.svg';
import SearchIcon from '@/fo/icons/base/icons/search.svg';
import {
  storeProduct,
  selectCounterWishlist,
  selectCounterCart,
  selectCounterCompare
} from '@/fo/store/product';
import { searchActions } from '@/fo/store/search-suggestion';
import { MenuItem, UiAppMenuBase } from '@/fo/ui/app/menu/base';
import { eventBannerHero } from '@/fo/ui/kit/banner/hero';
import {
  UiKitCounterBase,
  UiKitCounterBaseProps
} from '@/fo/ui/kit/counter/base';
import { UiModHeadFixed } from '@/fo/ui/mod/head/fixed';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { UiModSliderFading } from '@/fo/ui/mod/slider/fading';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventCartModal } from '@/fo/util/event-cart-modal';

import { styles } from './UiAppHeaderBase.styles';

type Variants = VariantProps<typeof styles>;
export interface UiAppHeaderBaseProps {
  color?: Variants['color'];
  informer?: string;
  links?: { href: string; label: string; onClick?: () => void }[];
  menu: {
    categories: MenuItem[];
  };
  isAuthorized: boolean;
  customVariables?: {
    [key: string]: string | unknown[];
  };
  searchSuggestion?: React.ReactNode;
}

export function UiAppHeaderBase({
  color: colorProp = 'dark',
  informer,
  menu,
  isAuthorized,
  customVariables,
  searchSuggestion
}: UiAppHeaderBaseProps) {
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations();

  const refTimer = useRef(0);

  const isMobile = useMediaQuery('md', 'max');

  const [isFixed, setIsFixed] = useState(false);
  const [isMount, setIsMount] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [activeMenu, setActiveMenu] = useState({
    index: 0,
    offsetLeft: 0
  });
  const refHeroBannerColor = useRef<Variants['color']>(colorProp);
  const [color, setColor] = useState<Variants['color']>(colorProp);
  const [hasHeroBanner, setHasHeroBanner] = useState(true);
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const showOverlayMenu = isMobile
    ? false
    : activeMenu.index > -1 || showSearch;

  // Не делайте так, это плохой пример, не используйте store в ui компонентах
  const wishlistCount = useSelector(storeProduct, selectCounterWishlist);
  const compareCount = useSelector(storeProduct, selectCounterCompare);
  const cartCount = useSelector(storeProduct, selectCounterCart);

  const icons: Icon[] = [
    {
      icon: 'search',
      onClick: () => handleShowSearch()
    },
    {
      icon: 'favorite',
      counter: wishlistCount,
      onClick: () =>
        isAuthorized
          ? router.push('/account/wishlist')
          : utilEventAuthModal.open('header')
    },
    {
      icon: 'compare',
      counter: compareCount,
      onClick: () =>
        isAuthorized
          ? router.push('/account/compare')
          : utilEventAuthModal.open('header')
    },
    {
      icon: 'profile',
      counter: 1,
      noLimit: true,
      onClick: () =>
        isAuthorized
          ? router.push('/account')
          : utilEventAuthModal.open('header')
    },
    {
      icon: 'cart',
      counter: cartCount,
      noLimit: true,
      onClick: () => utilEventCartModal.open()
    }
  ];

  useEffect(() => {
    setShowSearch(false);
    setActiveMenu({ index: 0, offsetLeft: 0 });
    setColor(refHeroBannerColor.current);

    const heroBanner = document.querySelector('.banner-hero');

    if (heroBanner) {
      setHasHeroBanner(true);
    } else {
      setHasHeroBanner(false);
    }
  }, [isMobile, pathname]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowSearch(false);
        setActiveMenu({ index: -1, offsetLeft: 0 });
        setColor(refHeroBannerColor.current);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  useEffect(() => {
    const modeToColor = {
      dark: 'base',
      light: 'transparent',
      off: colorProp
    } as const;
    return eventBannerHero.onModeChange((mode = 'off') => {
      refHeroBannerColor.current = modeToColor[mode] || colorProp;
      if (!showOverlayMenu) {
        setColor(refHeroBannerColor.current);
      }
    });
  }, [colorProp, showOverlayMenu]);

  const colorCounter: UiKitCounterBaseProps['color'] =
    color === 'transparent' && !isFixed ? 'light' : 'primary';

  const slots = styles({ color, fixed: isFixed });

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsMount(true);
    });
    return () => {
      clearTimeout(refTimer.current);
    };
  }, []);

  const iconsNavigate = {
    search: () => (
      <SearchIcon className={slots.iconFill({ color: checkColor() })} />
    ),
    favorite: () => (
      <HeartIcon className={slots.iconStroke({ color: checkColor() })} />
    ),
    compare: () => (
      <CompareIcon className={slots.iconStroke({ color: checkColor() })} />
    ),
    profile: () => (
      <UserIcon className={slots.iconStroke({ color: checkColor() })} />
    ),
    cart: () => <BagIcon className={slots.iconFill({ color: checkColor() })} />
  };

  type Icon = {
    icon: keyof typeof iconsNavigate;
    label?: string;
    counter?: number;
    noLimit?: boolean;
    onClick?: () => void;
  };

  const handleChangeMenu = (state: typeof activeMenu, delay = 500) => {
    if (showSearch) {
      setShowSearch(false);
    }
    clearTimeout(refTimer.current);
    refTimer.current = window.setTimeout(() => {
      if (state.index > -1) {
        setColor('light');
      } else {
        setColor(refHeroBannerColor.current);
      }
      setActiveMenu((s) => ({ ...s, ...state }));
    }, delay);
  };

  const handleHideMenu = () => {
    handleChangeMenu({ index: -1, offsetLeft: 0 });
    setIsMenuVisible(false);
  };

  const handleShowSearch = () => {
    const next = !showSearch;
    setShowSearch(next);
    setIsMenuVisible(false);

    if (next) {
      searchActions.fillDefaultData();
    }
  };

  const handleMenuVisibility = (value: boolean) => {
    setIsMenuVisible(value);
  };

  const checkColor = () => {
    if (pathname.includes('catalog') || pathname.includes('articles')) {
      return !isMenuVisible && !showSearch ? 'light' : 'dark';
    }

    if (hasHeroBanner || showSearch) {
      return 'dark';
    }

    return !isMenuVisible ? 'light' : 'dark';
  };

  const showHeaderBg = () => {
    if (
      pathname.includes('catalog') &&
      hasHeroBanner &&
      !isMenuVisible &&
      !showSearch
    ) {
      return true;
    }

    if (
      pathname.includes('articles') &&
      hasHeroBanner &&
      !isMenuVisible &&
      !showSearch
    ) {
      return true;
    }

    if (!hasHeroBanner && !isMenuVisible && !showSearch) {
      return true;
    }

    return false;
  };

  const topRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const topLogoRef = useRef<HTMLDivElement | null>(null);

  // Императивно изменяя стили мы ускоряем их применение к элементам хедера
  const handleChangeFixed = (isFixed: boolean) => {
    setIsFixed(isFixed); // Чтобы после rerender синхронизировать стили в jsx элементах
    setShowSearch(false);
    const newSlots = styles({
      color,
      fixed: isFixed
    });

    if (topRef.current) {
      topRef.current.classList.remove(
        ...slots
          .headerWrapper({
            className: isMount ? '' : 'max-md:opacity-0'
          })
          .split(' ')
      );

      topRef.current.classList.add(
        ...newSlots
          .headerWrapper({
            className: isMount ? '' : 'max-md:opacity-0'
          })
          .split(' ')
      );
    }

    if (mainRef.current) {
      mainRef.current.classList.remove(
        ...slots
          .main({
            className: isMount ? '' : 'opacity-0'
          })
          .split(' ')
      );

      mainRef.current.classList.add(
        ...newSlots
          .main({
            className: isMount ? '' : 'opacity-0'
          })
          .split(' ')
      );
    }

    if (topLogoRef.current) {
      topLogoRef.current.classList.remove(...slots.topLogo().split(' '));

      topLogoRef.current.classList.add(...newSlots.topLogo().split(' '));
    }
  };

  useEffect(() => {
    if (topRef.current?.clientHeight) {
      handleChangeFixed(topRef.current?.clientHeight <= window.pageYOffset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {informer && (
        <UiModSliderFading
          className={slots.informer({
            className: isMount ? '' : 'max-md:opacity-0'
          })}
        >
          {informer}
        </UiModSliderFading>
      )}
      {/* MENU:{isShowMenu} */}
      <UiModHeadFixed
        ref={topRef}
        className={`${slots.headerWrapper()} ${isMenuVisible || showSearch ? slots.menuOpen() : ''} ${showHeaderBg() ? slots.menuWithBg() : ''}`}
        onFixedChange={handleChangeFixed}
        order={0}
      >
        <div
          className={slots.top({
            className: isMount ? '' : 'max-md:opacity-0'
          })}
        >
          <div className={slots.container()}>
            <div
              className={slots.topLeftLink({
                className: isMount ? '' : 'opacity-0'
              })}
            >
              {customVariables?.phone && (
                <Link
                  href={`tel: ${customVariables?.phone}`}
                  className={slots.contactPhone({
                    color: checkColor()
                  })}
                >
                  {customVariables?.phone as string}
                </Link>
              )}
              <Link
                href={`/shops`}
                className={slots.stores({
                  color: checkColor()
                })}
              >
                {t('header.shops')}
              </Link>
              <Link
                href={`/catalog`}
                className={slots.catalogLink()}
              >
                {t('header.catalog')}
              </Link>
            </div>
            <div className={slots.topLogo()}>
              <Link
                href="/"
                className={slots.logoBrand()}
              >
                <BrandLogo
                  className={slots.logoBrandIcon({
                    color: checkColor()
                  })}
                />
              </Link>
            </div>
            <div
              className={slots.topRightLink({
                className: isMount ? '' : 'opacity-0'
              })}
            >
              {icons.map((icon, index) => (
                <div
                  key={`${icon.icon}-${index}`}
                  className={slots.linkIcon()}
                  onClick={icon.onClick}
                >
                  {iconsNavigate[icon.icon]()}
                  {icon.icon !== 'profile' && (
                    <UiKitCounterBase
                      color={colorCounter}
                      noLimit={icon.noLimit}
                      className={slots.linkCounter({
                        className: icon.icon !== 'cart' ? 'md:left-4.5' : ''
                      })}
                      counter={icon.counter}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={slots.main({ className: isMount ? '' : 'opacity-0' })}>
          <div className={slots.container()}>
            <ArrowIcon
              className={slots.backArrow()}
              // onClick={onBackClick}
            />
            <div className={slots.linksLeft()}>
              {activeMenu.index === 0 && (
                <UiAppMenuBase
                  items={menu.categories}
                  style={{ marginLeft: `${activeMenu.offsetLeft}px` }}
                  color={checkColor()}
                  onShow={() => handleChangeMenu(activeMenu)}
                  onHide={handleHideMenu}
                  handleMenuVisibility={handleMenuVisibility}
                />
              )}
            </div>
          </div>
        </div>
      </UiModHeadFixed>
      {!showSearch && (
        <div
          id="UiAppMenuBase-children-0"
          className={slots.navbar({
            color: checkColor()
          })}
        ></div>
      )}

      {showSearch && (
        <div
          className={slots.navbar({
            color: checkColor()
          })}
        >
          <div className={slots.searchSuggestion()}>
            <div
              onClick={() => handleShowSearch()}
              className={slots.searchSuggestionOverlay()}
            ></div>
            <div className={slots.searchSuggestionContent()}>
              {searchSuggestion}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
